import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDownToLine, Banknote, Clock, Play, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, Money } from '@/components/ui';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { navigationFromState } from '@/utils/navigateBack';
import GameWithdrawModal from '@/features/finance/components/withdraw/GameWithdrawModal';
import type { GameWalletItem } from '@/features/finance/services/paymentService';
import { getGalleryForProvider, hasIconForProvider, type GameProvider } from '@/features/games/data/gameProviders';
import { GameGalleryTile } from './GameGalleryTile';
import { isProviderDepositLocked } from '@/features/games/services/gameProviderHealthService';
import { usePlayGame } from '@/features/games/hooks/usePlayGame';
import GameLogin from './GameLogin';

export interface GameSheetProps {
  /** The game to show; null closes the sheet. */
  game: GameProvider | null;
  /** The player's account for this game, if any (linked when it has a game username). */
  wallet?: GameWalletItem | null;
  onClose: () => void;
  /** Money moved from the game to My money, or a move was asked of our team (refresh balances). */
  onWithdrawn?: () => void;
}

const FALLBACK_ICON = '/icons/egame.jpg';
const formatUsd = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

/**
 * The game sheet (spec §4), opened from Play, Money and My games: art, notes, and when the player
 * has the game its balance and login. Add money starts the guided flow with this game chosen
 * ("Get this game" when the player does not have it yet); Take money out (only with a balance)
 * runs today's game → wallet popup, then says where the money is with a Cash out button.
 * Off kiosks, a playable game's Play (usePlayGame) is the emphasised action, after the others.
 */
export default function GameSheet({ game, wallet = null, onClose, onWithdrawn }: GameSheetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { requireAuth } = useAuthModal();
  const { canPlay, play, playDialog } = usePlayGame();

  /** Last shown game and account, so the sheet keeps its content while it animates closed. */
  const [last, setLast] = useState<{ game: GameProvider; wallet: GameWalletItem | null } | null>(
    game ? { game, wallet } : null
  );
  useEffect(() => {
    if (game) setLast({ game, wallet });
  }, [game, wallet]);
  const shown = game ? { game, wallet } : last;

  /**
   * The game → My money popup runs instead of the sheet; the sheet comes back when money moved
   * (`moved`: now in My money) or a move was asked of our team (`requested`: not in My money yet).
   */
  const [withdrawFor, setWithdrawFor] = useState<string | null>(null);
  const [moved, setMoved] = useState(0);
  const [requested, setRequested] = useState(0);
  const movedRef = useRef(0);
  const requestedRef = useRef(0);
  const gameId = game?.id;
  const resetMoves = useCallback(() => {
    movedRef.current = 0;
    requestedRef.current = 0;
    setMoved(0);
    setRequested(0);
  }, []);
  useEffect(() => {
    if (!gameId) return;
    resetMoves();
  }, [gameId, resetMoves]);

  const shownGame = shown?.game ?? null;
  const shownWallet = shown?.wallet ?? null;
  const linked = !!shownWallet?.account?.gameUsername;
  const balance = Number(shownWallet?.balance?.inGameBalance ?? 0) || 0;
  const locked = shownGame ? isProviderDepositLocked(shownGame) : false;
  const iconSrc = shownGame && hasIconForProvider(shownGame.code) ? shownGame.icon : FALLBACK_ICON;
  const gallery = getGalleryForProvider(shownGame?.code);
  /** Non-kiosk only (usePlayGame): a linked game with an http(s) link that is not under maintenance. */
  const playable = !!shownGame && canPlay(shownWallet, shownGame);

  const playGame = () => {
    if (!shownWallet) return;
    // The in-app player is a page of its own: the sheet closes. "Your login" opens over the sheet.
    if (shownWallet.game.playInFrame === true) onClose();
    play(shownWallet);
  };

  const addMoney = () => {
    if (!shownGame) return;
    const id = shownGame.id;
    onClose();
    requireAuth(() => {
      navigate(`/money/add?game=${encodeURIComponent(id)}`, navigationFromState(location.pathname));
    });
  };

  const takeMoneyOut = () => {
    if (!shownGame) return;
    const id = shownGame.id;
    requireAuth(() => {
      resetMoves();
      setWithdrawFor(id);
    });
  };

  const closeWithdraw = () => {
    setWithdrawFor(null);
    // Nothing moved or asked for: the sheet stays closed, as when the popup was opened from it before.
    if (movedRef.current <= 0 && requestedRef.current <= 0) onClose();
  };

  const cashOut = () => {
    onClose();
    navigate('/money/cash-out');
  };

  return (
    <>
      <Dialog
        open={!!game && withdrawFor == null}
        onClose={onClose}
        title={shownGame?.name}
        // Unlinked: what "Get this game" does. A linked game needs no subtitle (balance and login are below).
        description={shownGame && !linked ? t('games.sheet.getGameHint') : undefined}
        size="lg"
        closeLabel={t('common.close')}
        footer={
          shownGame ? (
            <>
              {linked && balance > 0 ? (
                <Button variant="payout" size="lg" icon={<ArrowDownToLine />} onClick={takeMoneyOut}>
                  {t('games.sheet.takeMoneyOut')}
                </Button>
              ) : null}
              <Button variant="primary" size="lg" icon={<Plus />} disabled={locked} onClick={addMoney}>
                {linked ? t('games.sheet.addMoney') : t('games.sheet.getGame')}
              </Button>
              {/* Last child: on top of the stacked footer on phones, rightmost on wider screens. */}
              {playable ? (
                <Button variant="primary" size="lg" emphasis icon={<Play />} onClick={playGame}>
                  {t('play.play')}
                </Button>
              ) : null}
            </>
          ) : null
        }
      >
        {shownGame ? (
          <div key={shownGame.id} className="flex min-w-0 flex-col gap-4">
            {moved > 0 ? (
              <Alert
                tone="success"
                title={t('games.sheet.movedTitle', { amount: formatUsd(moved) })}
                action={
                  <Button variant="payout" size="lg" icon={<Banknote />} onClick={cashOut}>
                    {t('games.sheet.cashOut')}
                  </Button>
                }
              >
                {t('games.sheet.cashOutNow')}
              </Alert>
            ) : null}
            {requested > 0 ? (
              <Alert tone="info" title={t('games.sheet.requestedTitle', { amount: formatUsd(requested) })}>
                {t('games.sheet.requestedBody')}
              </Alert>
            ) : null}

            <div className="flex min-w-0 items-center gap-3">
              <div className="relative size-18 shrink-0 overflow-hidden rounded-container border border-edge-subtle bg-raised">
                <img
                  src={iconSrc}
                  alt=""
                  className="block h-full w-full object-cover pointer-events-none select-none"
                  draggable={false}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = FALLBACK_ICON;
                  }}
                />
              </div>
              {shownGame.tagline ? (
                <p className="m-0 min-w-0 font-ui text-small text-secondary">{shownGame.tagline}</p>
              ) : null}
            </div>

            {linked ? (
              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex min-w-0 items-center justify-between gap-3 rounded-control border border-edge-subtle bg-inset px-3 py-2">
                  <span className="font-ui text-small text-secondary">{t('games.sheet.balance')}</span>
                  <Money amount={balance} currency={shownWallet?.game?.currency || 'USD'} direction="out" size="title" />
                </div>
                <GameLogin username={shownWallet?.account?.gameUsername} password={shownWallet?.account?.password} />
              </div>
            ) : null}

            <div className="grid w-full min-w-0 grid-cols-3 gap-2" data-testid="game-sheet-gallery">
              {[0, 1, 2].map((i) =>
                gallery[i] ? (
                  <GameGalleryTile
                    key={gallery[i]}
                    src={gallery[i]}
                    iconSrc={hasIconForProvider(shownGame.code) ? shownGame.icon : undefined}
                  />
                ) : (
                  <div
                    key={i}
                    className="aspect-[178/105] min-w-0 overflow-hidden rounded-control border border-edge-subtle bg-inset"
                  >
                    {hasIconForProvider(shownGame.code) ? (
                      <img src={shownGame.icon} alt="" className="h-full w-full object-cover opacity-90" />
                    ) : (
                      <div className="h-full w-full bg-raised" />
                    )}
                  </div>
                )
              )}
            </div>

            {shownGame.depositType === 'manual' && (
              <p className="m-0 inline-flex items-center gap-1.5 font-ui text-small text-warning">
                <Clock className="size-3.5 shrink-0" aria-hidden />
                {t('games.sheet.manualNote', { name: shownGame.name })}
              </p>
            )}

            {locked && (
              <p
                className="m-0 inline-flex items-center gap-1.5 font-ui text-small text-warning"
                data-testid="game-detail-maintenance"
              >
                <Clock className="size-3.5 shrink-0" aria-hidden />
                {t('games.sheet.maintenanceNote')}
              </p>
            )}
          </div>
        ) : null}
      </Dialog>

      <GameWithdrawModal
        open={withdrawFor != null}
        onClose={closeWithdraw}
        focusProviderId={withdrawFor}
        onSuccess={(amount, asked = 0) => {
          movedRef.current += amount;
          requestedRef.current += asked;
          setMoved(movedRef.current);
          setRequested(requestedRef.current);
          onWithdrawn?.();
        }}
      />

      {playDialog}
    </>
  );
}
